import styled, { css } from 'styled-components';

import colors from 'utils/colors';
import * as Styling from 'utils/styled';

import { MenuItem, ISideNavContextProps } from '../interfaces';

const SideNavLinkLevel_0 = colors.darkGrey;
const SideNavLinkLevel_1 = colors.SubGrey;
const SideNavLinkLevel_2 = colors.blueGrey;
const SideNavLinkLevel_3 = colors.blueGrey75;

interface StyledLabelProps extends MenuItem {
  index: number;
}

interface StyledTextProps extends MenuItem {
  isItemOpen: boolean;
}

interface StyledIconProps extends MenuItem {
  isItemOpen: boolean;
}

export const ListWrapper = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  font-weight: 400;
  display: flex;
  flex-direction: column;
  min-width: 100%;
`;

export const ItemWrapper = styled.li`
  margin: 0;
  display: flex;
  flex-direction: column;
`;

export const FooterWrapper = styled.div`
  margin-top: 25px;
  height: 60px;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  min-width: 100%;

  a,
  button {
    box-sizing: border-box;
    width: 100%;
  }
`;

export const HeadWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  margin-top: 25px;
  margin-bottom: 25px;
  cursor: pointer;
`;

export const StyledLogo = styled.div`
  padding-left: 24px;
  flex-basis: 85%;

  a {
    width: 100%;
  }
`;

export const StyledClose = styled.a`
  cursor: pointer;
  flex-basis: 15%;

  svg {
    width: 20px;
    height: 20px;
  }

  :hover,
  :focus {
    path {
      cursor: pointer;
      fill: ${colors.white};
    }
  }
`;

export const StyledMenu = styled.nav<Pick<ISideNavContextProps, 'isOpen'>>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  flex-grow: 1;
  max-width: 300px;
  min-width: 300px;
  z-index: 10;
  height: 100vh;
  background-color: ${colors.darkGrey};
  overflow: hidden;

  ${props => (props.isOpen ? Styling.transitionVisible : Styling.transitionHidden)};
`;

export const MenuOffset = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  overflow-x: hidden;
  overflow-y: scroll;

  > ul {
    border-bottom: 2px solid ${colors.darkGrey75};
  }
`;

export const TextLevelZero = css<StyledTextProps>`
  padding-left: 24px;
  font-size: 18px;
  font-weight: 700;
`;

export const TextLevelOne = css<StyledTextProps>`
  padding-left: 28px;
  font-size: 15px;
  font-weight: ${props => (props.isItemOpen ? 'bold' : 'normal')};
  &:hover {
    text-decoration: underline;
  }
`;

export const TextLevelTwo = css<StyledTextProps>`
  padding-left: 43px;
  font-size: 14px;
  font-weight: ${props => (props.isItemOpen ? 'bold' : 'normal')};
  &:hover {
    text-decoration: underline;
  }
`;

export const TextLevelThree = css<StyledTextProps>`
  padding-left: 57px;
  padding-right: 10px;
  font-size: 14px;
  font-weight: ${props => (props.isItemOpen ? 'bold' : 'normal')};
  &:hover {
    text-decoration: underline;
  }
`;

export const StyledText = styled.a<StyledTextProps>`
  text-decoration: none;
  color: ${colors.white};
  line-height: 1.2;
  border: none;
  ${props =>
    props.itemLevel === 0
      ? TextLevelZero
      : props.itemLevel === 1
      ? TextLevelOne
      : props.itemLevel === 2
      ? TextLevelTwo
      : TextLevelThree}
`;

export const StyledIconExpanded = styled.a<StyledIconProps>`
  display: flex;
  justify-content: center;
  flex-basis: 20%;
  cursor: pointer;
  font-weight: 400;

  ${props =>
    props.isItemOpen &&
    `
  background-color: ${colors.blueGrey75};
  display: flex;
  justify-content: center;
  align-self: stretch;
  padding-top: 20px;
  `}
  ${props =>
    props.itemLevel === 0 && props.isItemOpen && `background-color: ${SideNavLinkLevel_1};`}

  ${props =>
    props.itemLevel === 1 && props.isItemOpen && `background-color: ${SideNavLinkLevel_2};`}


  ${props =>
    props.itemLevel === 2 && props.isItemOpen && `background-color: ${SideNavLinkLevel_3};`}

  svg {
    width: 20px;
    height: 20px;
    background-color: ${colors.white};
    border: 2px solid ${colors.white};
    border-radius: 12px;

    path {
      fill: ${colors.darkGrey};
    }
  }
`;

export const StyledIconCollapsed = styled.a<StyledIconProps>`
  display: flex;
  justify-content: center;
  flex-basis: 20%;
  cursor: pointer;
  font-weight: 400;

  svg {
    width: 15px;
    height: 15px;
    border: 2px solid ${colors.white};
    border-radius: 12px;
    padding: 3px;
    box-sizing: content-box;

    path {
      fill: ${colors.white};
    }

    &:before,
    &:after {
      box-sizing: content-box;
    }
  }
`;

export const LabelWrapper = styled.div<StyledLabelProps>`
  display: flex;
  flex-direction: row;
  min-height: 63.5px;
  justify-content: space-between;
  align-items: center;
  background-color: ${props =>
    props.itemLevel === 0
      ? SideNavLinkLevel_0
      : props.itemLevel === 1
      ? SideNavLinkLevel_1
      : props.itemLevel === 2
      ? SideNavLinkLevel_2
      : SideNavLinkLevel_3};

  border-top: ${props =>
    props.index === 0
      ? ''
      : props.itemLevel === 0
      ? `1px solid ${colors.darkGrey75}`
      : props.itemLevel === 1
      ? `1px solid ${colors.blueGrey}`
      : props.itemLevel === 2
      ? `1px solid ${colors.blueGrey75}`
      : `1px solid ${colors.blueGrey50};`};
`;

export const SubItemsWrapper = styled.div`
  display: block;
  width: 100%;
`;
